import { useQuery } from '@tanstack/react-query'
import { CalendarOff } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { FormField } from '@/components/forms/FormField'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { useAuth } from '@/features/auth/useAuth'
import { familyLeaveScope } from '@/features/leave/api'
import { LeaveSelfService } from '@/features/leave/components/LeaveSelfService'
import { familyQuery } from '@/features/portal/api'
import { familyPortalLabel, useGuardians } from '@/features/auth/guardians'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { tAny } from '@/i18n'

/**
 * Family portal → Absence requests: a student for themselves, a guardian for one of their children.
 * Same workflow as staff leave, worded as permission to be absent.
 */
export function FamilyLeavePage() {
  const { t } = useTranslation('leave')
  const guardians = useGuardians()
  useDocumentTitle('Absence requests')
  const { user } = useAuth()
  const children = useQuery(familyQuery())
  const [selected, setSelected] = useState<number | null>(null)
  const isStudent = user?.role === 'student'

  const list = children.data ?? []
  const child = list.find((c) => c.student.id === selected) ?? list[0]

  return (
    <>
      <PageHeader
        title={t('Absence requests')}
        description={
          isStudent
            ? t('Ask the school for permission to be absent and follow your requests.')
            : t('Ask the school for permission for your child to be absent and follow its decision.')
        }
        breadcrumbs={[
          { label: tAny(familyPortalLabel(guardians)), to: '/portal' },
          { label: t('Absence requests') },
        ]}
        homeHref="/portal"
        actions={
          list.length > 1 ? (
            <FormField label={t('Child')} hideLabel className="w-full sm:w-64">
              <Select
                value={child?.student.id ?? ''}
                onChange={(e) => setSelected(Number(e.target.value))}
                options={list.map((c) => ({ value: c.student.id, label: c.student.fullName }))}
              />
            </FormField>
          ) : null
        }
      />
      {children.isPending ? (
        <LoadingState />
      ) : children.error ? (
        <ErrorState onRetry={() => void children.refetch()} />
      ) : !child ? (
        <EmptyState
          icon={<CalendarOff />}
          title={t('No student linked')}
          description={t('Ask the school office to link your account to a student.')}
        />
      ) : (
        <LeaveSelfService
          key={child.student.id}
          scope={familyLeaveScope(child.student.id)}
          wording="absence"
          subject={isStudent ? t('yourself') : child.student.fullName}
        />
      )}
    </>
  )
}
